import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import { Link, useParams } from "react-router";
import type { MeUserDataType } from "src/app";
import { getRelayErrorMessage } from "src/common/utils";
import { Header } from "src/components/header";
import { LeftNavigation } from "src/components/left-navigation";
import { ChatInput } from "src/components/ui/chat/chat-input";
import { ContentSuspense } from "src/components/ui/content-suspense";
import { PageSuspense } from "src/components/ui/page-suspense";
import { Toaster } from "src/components/ui/toaster";
import { NavigationProvider, useNavigation } from "src/context/NavigationContext";
import { usePusherEvent } from "src/hooks/use-pusher-event";
import { useToast } from "src/hooks/use-toast";

import LlmConversationMessageList from "../AskPage/components/LlmConversationMessageList";
import { useInterruptAgentMutation } from "../AskPage/hooks/useInterruptAgentMutation";
import { useSendLlmMessage } from "../AskPage/hooks/useSendLlmMessage";
import type { InteractionRedirectContent_llmConversation$key } from "./__generated__/InteractionRedirectContent_llmConversation.graphql";
import type { InteractionRedirectQuery } from "./__generated__/InteractionRedirectQuery.graphql";

const interactionRedirectQuery = graphql`
  query InteractionRedirectQuery($interactionId: String!) {
    me {
      ...appMe
      pictureUrl
      firstName
      lastName
      organization {
        id
      }
    }
    llmConversationByInteractionId(interactionId: $interactionId) {
      status
      interactionExists
      scheduledAt
      llmConversation {
        id
        source
        sourceId
        metadataJson
        createdAt
        updatedAt
        conversationId
        ...InteractionRedirectContent_llmConversation
      }
    }
  }
`;

const llmConversationFragment = graphql`
  fragment InteractionRedirectContent_llmConversation on GraphQLLlmConversation
  @refetchable(queryName: "InteractionRedirectContentRefetchQuery") {
    id
    source
    sourceId
    metadataJson
    conversationId
    createdAt
    updatedAt
    hasActiveInteraction
    messages {
      id
      messageRole
      messageType
      content
      messageMetadata {
        usage {
          billingTokens
        }
      }
    }
    ...LlmConversationMessageList_llmConversation
  }
`;

const InteractionNotFound: React.FC = () => (
  <div className="h-full flex items-center justify-center">
    <div className="text-center text-slate-400">
      <p className="text-lg mb-2">Interaction not found</p>
      <p className="text-sm">This interaction is not associated with a chat conversation.</p>
      <Link to="/chat" className="text-primary hover:text-primary/80 mt-4 inline-block">
        Back to Chat
      </Link>
    </div>
  </div>
);

interface InteractionProcessingProps {
  interactionId: string;
  status: string;
  scheduledAt?: string | null;
  onUpdate: () => void;
}

const InteractionProcessing: React.FC<InteractionProcessingProps> = ({
  interactionId,
  status,
  scheduledAt,
  onUpdate,
}) => {
  // Listen for Pusher updates on interaction channel to know when status changes
  usePusherEvent<{ interaction_id: string }>({
    channelId: `interaction-${interactionId}`,
    eventType: "INTERACTION_UPDATED",
    onEvent: useCallback(() => {
      onUpdate();
    }, [onUpdate]),
  });

  // Determine the message based on status
  const statusDisplay = useMemo(() => {
    switch (status) {
      case "PENDING":
        if (scheduledAt) {
          const scheduledDate = new Date(scheduledAt);
          const formattedDate = scheduledDate.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          });
          return {
            title: "Scheduled",
            message: `This interaction is scheduled to run at ${formattedDate}.`,
          };
        }
        return {
          title: "Queued",
          message: "Your request is queued and will begin processing shortly...",
        };
      case "FAILED":
        return {
          title: "Processing Failed",
          message: "There was an error processing your request. Please try again.",
          isError: true,
        };
      default:
        return {
          title: "Processing",
          message: "Your interaction is being processed...",
        };
    }
  }, [status, scheduledAt]);

  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center text-slate-400">
        <div className="mb-4">
          {statusDisplay.isError ? (
            <svg
              className="h-12 w-12 mx-auto text-red-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ) : (
            <svg
              className="animate-spin h-12 w-12 mx-auto text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
        </div>
        <p className={`text-lg mb-2 font-semibold ${statusDisplay.isError ? "text-red-400" : ""}`}>
          {statusDisplay.title}
        </p>
        <p className="text-sm">{statusDisplay.message}</p>
        {status && (
          <p className="text-xs mt-4 opacity-60">
            Status: <span className="font-mono">{status}</span>
          </p>
        )}
      </div>
    </div>
  );
};

interface InteractionChatContentProps {
  interactionId: string;
}

const InteractionChatContent: React.FC<InteractionChatContentProps> = ({ interactionId }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const data = useLazyLoadQuery<InteractionRedirectQuery>(
    interactionRedirectQuery,
    { interactionId },
    { fetchPolicy: "network-only", fetchKey: refreshKey }
  );

  const result = data.llmConversationByInteractionId;

  // Refetch function for PENDING state
  const handleRefetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Handle different states before setting up hooks
  if (!result.interactionExists) {
    return <InteractionNotFound />;
  }

  // Show processing state only for PENDING or FAILED without conversation
  if (result.status === "PENDING") {
    return (
      <InteractionProcessing
        interactionId={interactionId}
        status={result.status}
        scheduledAt={result.scheduledAt}
        onUpdate={handleRefetch}
      />
    );
  }

  // For IN_PROGRESS or FAILED, show the chat if we have a conversation
  if ((result.status === "IN_PROGRESS" || result.status === "FAILED") && result.llmConversation) {
    return <InteractionChat data={data} llmConversationData={result.llmConversation} interactionId={interactionId} />;
  }

  // For FAILED without conversation, show processing state
  if (result.status === "FAILED") {
    return (
      <InteractionProcessing
        interactionId={interactionId}
        status={result.status}
        scheduledAt={result.scheduledAt}
        onUpdate={handleRefetch}
      />
    );
  }

  if (!result.llmConversation) {
    return <InteractionNotFound />;
  }

  // If we have a conversation, render the chat component
  return <InteractionChat data={data} llmConversationData={result.llmConversation} interactionId={interactionId} />;
};

interface InteractionChatProps {
  data: InteractionRedirectQuery["response"];
  llmConversationData: NonNullable<
    InteractionRedirectQuery["response"]["llmConversationByInteractionId"]["llmConversation"]
  >;
  interactionId: string;
}

const InteractionChat: React.FC<InteractionChatProps> = ({ data, llmConversationData, interactionId }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [conversationDeleted, setConversationDeleted] = useState(false);

  // Use refetchable fragment for updates without Suspense
  const [llmConversation, refetch] = useRefetchableFragment(
    llmConversationFragment,
    llmConversationData as InteractionRedirectContent_llmConversation$key
  );

  // Create refetch function that doesn't trigger Suspense
  const refetchLlmConversation = useCallback(() => {
    startTransition(() => {
      refetch({}, { fetchPolicy: "network-only" });
    });
  }, [refetch]);

  if (!llmConversation) {
    return <InteractionNotFound />;
  }

  // Store the llm_conversation_id for this interaction_id
  useEffect(() => {
    if (llmConversation?.id && interactionId) {
      try {
        const storageKey = `interaction_conversation_${interactionId}`;
        localStorage.setItem(storageKey, llmConversation.id);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [llmConversation?.id, interactionId]);

  const userData = {
    pictureUrl: data.me?.pictureUrl || undefined,
    firstName: data.me?.firstName,
    lastName: data.me?.lastName,
  };

  const userInitials = `${userData?.firstName?.[0] ?? ""}${userData?.lastName?.[0] ?? ""}`;
  const { toast } = useToast();

  // Check if conversation is read-only
  const isReadOnly = useMemo(() => {
    if (!llmConversation || !llmConversation.source) {
      return true;
    }
    return llmConversation.source !== "UI";
  }, [llmConversation]);

  // Send message handling with optimistic UI
  const { sendMessage, isSending } = useSendLlmMessage({ llmConversationId: llmConversation.id });

  // Check if there are any pending or in-progress interactions
  const hasActiveInteraction = llmConversation.hasActiveInteraction;

  // For backwards compatibility, we still need activeInteractionId for the message list
  // This will be undefined when using hasActiveInteraction, which is acceptable
  const activeInteractionId = undefined;

  const handleSend = useCallback(
    async (messageText: string) => {
      const trimmedMessage = messageText.trim();
      if (!trimmedMessage) return;

      try {
        await sendMessage(trimmedMessage);
        // Refetch to show the USER_INPUT message immediately
        refetchLlmConversation();
      } catch (error) {
        const errorMessage = error instanceof Error ? getRelayErrorMessage(error) : "Failed to send message";
        toast({
          description: errorMessage,
          variant: "error",
        });
      }
    },
    [sendMessage, toast, refetchLlmConversation]
  );

  // Check if the last message is complete (has metadata and usage data)
  const isLastMessageComplete = useMemo(() => {
    const messages = llmConversation.messages;
    if (!messages || messages.length === 0) return false;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return false;

    // Check if it's an agent message with metadata (indicating completion)
    const isAgentMessage = lastMessage.messageRole === "AGENT";
    if (!isAgentMessage) return false;

    // If it's an AGENT_PROCESS message (intermediary), it's not complete
    if (lastMessage.messageType === "AGENT_PROCESS") return false;

    // Check for metadata and content
    const hasMetadata = Boolean(lastMessage.messageMetadata);
    const hasContent = Boolean(lastMessage.content && lastMessage.content.length > 0);

    // Message is complete if it has metadata, content, AND it's a final response (not AGENT_PROCESS)
    return hasMetadata && hasContent;
  }, [llmConversation.messages]);

  // Sending is disabled if we're actively sending OR if there's an active interaction AND last message is not complete
  const combinedSendingState = isSending || (hasActiveInteraction && !isLastMessageComplete);

  // Interrupt agent mutation
  const { handleInterruptAgent } = useInterruptAgentMutation();
  const [isInterrupting, setIsInterrupting] = useState(false);

  // Clear interrupting state when there's no active interaction
  useEffect(() => {
    if (!hasActiveInteraction && isInterrupting) {
      setIsInterrupting(false);
    }
  }, [hasActiveInteraction, isInterrupting]);

  // Handle interrupt
  const handleInterrupt = useCallback(() => {
    setIsInterrupting(true);
    handleInterruptAgent(llmConversation.id, undefined, llmConversation.id, activeInteractionId);
  }, [llmConversation.id, activeInteractionId, handleInterruptAgent]);

  // Listen for Pusher updates on conversation channel
  usePusherEvent<{ llm_conversation_id: string }>({
    channelId: llmConversation.id,
    eventType: "LLM_CONVERSATION_UPDATED",
    onEvent: useCallback(() => {
      refetchLlmConversation();
    }, [refetchLlmConversation]),
  });

  // Also listen for Pusher updates on interaction channel
  usePusherEvent<{ interaction_id: string }>({
    channelId: `interaction-${interactionId}`,
    eventType: "INTERACTION_UPDATED",
    onEvent: useCallback(() => {
      refetchLlmConversation();
    }, [refetchLlmConversation]),
  });

  // Listen for conversation deletion on organization channel
  const organizationId = data.me?.organization?.id;
  usePusherEvent<{ llm_conversation_id: string }>({
    channelId: organizationId ? `organization-${organizationId}` : undefined,
    eventType: "LLM_CONVERSATION_DELETED",
    onEvent: useCallback(
      (eventData) => {
        // Check if this is our conversation being deleted
        if (eventData.llm_conversation_id === llmConversation.id) {
          setConversationDeleted(true);
        }
      },
      [llmConversation.id]
    ),
  });

  // If conversation was deleted, show appropriate message
  if (conversationDeleted) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-slate-400">
          <p className="text-lg mb-2">Conversation Deleted</p>
          <p className="text-sm">This conversation has been deleted.</p>
          <Link to="/chat" className="text-primary hover:text-primary/80 mt-4 inline-block">
            Back to Chat
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-midnight relative z-10 min-h-full">
      {/* Messages container with gradient overlay */}
      <div className="flex-1 overflow-hidden relative">
        <LlmConversationMessageList
          llmConversation={llmConversation}
          userAvatar={userData?.pictureUrl}
          userInitials={userInitials}
          messagesEndRef={messagesEndRef}
          isPending={combinedSendingState}
          activeInteractionId={activeInteractionId}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-midnight via-midnight/80 to-transparent z-10"
        />
      </div>

      {/* Chat input - Floating sticky at bottom */}
      {!isReadOnly && (
        <div className="sticky bottom-0 pb-8 pt-3 px-8 flex justify-center z-20 pointer-events-none">
          <div className="w-full max-w-6xl pointer-events-auto">
            <form className="relative rounded-md bg-popover border border-border p-1.5 shadow-sm focus-within:border-primary/50 transition-colors duration-150">
              <ChatInput
                placeholder="Ask anything about your application or infrastructure"
                sendDisabled={combinedSendingState}
                onSend={handleSend}
                showInterrupt={combinedSendingState}
                onInterrupt={handleInterrupt}
                isInterrupting={isInterrupting}
                className="min-h-[40px] w-full resize-none border-0 bg-transparent p-3 text-foreground shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </form>
          </div>
        </div>
      )}

      {/* Read-only mode indicator */}
      {isReadOnly && (
        <div className="p-4 relative flex-shrink-0 border-t border-slate-600/70">
          <div className="flex items-center justify-center text-slate-400 text-sm">
            <span>This conversation is in read-only mode</span>
          </div>
        </div>
      )}
    </div>
  );
};

const InteractionPageContent: React.FC<{ interactionId: string }> = ({ interactionId }) => {
  return (
    <div className="flex-1 flex flex-col h-full relative max-h-full overflow-y-auto overflow-x-hidden w-full">
      <div className="absolute inset-0 bg-gradient-to-br dark:from-slate-900/5 dark:to-slate-950/10 pointer-events-none"></div>
      <InteractionChatContent interactionId={interactionId} />
    </div>
  );
};

function InteractionPageLayoutContent({ page, me }: { page: React.ReactElement; me: MeUserDataType }) {
  const { isLeftNavCollapsed } = useNavigation();

  return (
    <div className="h-screen flex bg-midnight overflow-hidden">
      <LeftNavigation isCollapsed={isLeftNavCollapsed} />
      <div className="flex flex-col flex-1 h-screen">
        <Header me={me} />
        <main className="flex-1 flex flex-col h-full relative max-h-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br dark:from-slate-900/5 dark:to-slate-950/10 pointer-events-none"></div>
          {page}
        </main>
      </div>
      <Toaster />
    </div>
  );
}

export const getInteractionPageLayout = (page: React.ReactElement, me: MeUserDataType) => {
  return (
    <NavigationProvider>
      <InteractionPageLayoutContent page={page} me={me} />
    </NavigationProvider>
  );
};

const InteractionRedirectWrapper: React.FC = () => {
  const params = useParams<{ id: string }>();
  const interactionId = params.id!;

  return (
    <PageSuspense>
      <ContentSuspense>
        <InteractionPageContent interactionId={interactionId} />
      </ContentSuspense>
    </PageSuspense>
  );
};

const InteractionRedirect = Object.assign(InteractionRedirectWrapper, {
  getLayout: getInteractionPageLayout,
});

export default InteractionRedirect;
